<template>
  <v-container class="page-container no-max pa-0">
    <!--#2776: minimize the height so the event-title is close to the top-->
    <c-loader-status
      :loaders="{
        'no-initial-content no-error-content': [purchase.$load],
      }"
      :height="2"
    >
      <v-expand-transition group mode="out-in">
        <purchase-create
          v-if="purchase.status == PurchaseStatus.Started"
          :purchase="purchase"
          :service="service"
        />

        <div v-else>
          <purchase-receipt-print :purchase="purchase" />
          <purchase-details
            :purchase="purchase"
            :service="service"
            class="no-print"
          />
        </div>
      </v-expand-transition>
    </c-loader-status>
  </v-container>
</template>

<script setup lang="ts">
import PurchaseCreate from "@public/components/purchase-create.vue";
import PurchaseDetails from "@public/components/purchase-details.vue";
import PurchaseReceiptPrint from "@common/purchase/purchase-receipt-print.vue";

import {
  TicketPurchaseDtoViewModel,
  PublicPurchaseServiceViewModel,
} from "@public/viewmodels.g";
import { PurchaseStatus } from "@public/models.g";
import { propDisplay } from "coalesce-vue";
import { scrollToTop, suppressPurchaseScroll } from "@common/utils";
import { differenceInMilliseconds } from "date-fns";
import { useTitle } from "@common/composables/useTitle";
import { onBeforeRouteLeave } from "vue-router";
import { PublicPurchaseServiceApiClient } from "@public/api-clients.g";

const service = new PublicPurchaseServiceViewModel();
const purchase = new TicketPurchaseDtoViewModel();
const apiClient = new PublicPurchaseServiceApiClient();

const props = defineProps<{
  publicId: string;
}>();

let userConfirmedLeave = false;

function sendAbandonBeacon(): void {
  const params = new URLSearchParams({ publicId: props.publicId });
  const sent = navigator.sendBeacon(
    "/api/PublicPurchaseService/AbandonPurchase",
    params,
  );
  if (!sent) {
    fetch("/api/PublicPurchaseService/AbandonPurchase", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: params.toString(),
      keepalive: true,
    });
  }
}

function onBeforeUnload(): void {
  if (purchase.status === PurchaseStatus.Started) {
    sendAbandonBeacon();
  }
}

function onPageHide(): void {
  if (purchase.status === PurchaseStatus.Started) {
    sendAbandonBeacon();
  }
}

function onVisibilityChange(): void {
  if (
    document.visibilityState === "hidden" &&
    purchase.status === PurchaseStatus.Started &&
    userConfirmedLeave
  ) {
    sendAbandonBeacon();
  }
}

onMounted(() => {
  window.addEventListener("beforeunload", onBeforeUnload);
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibilityChange);
});

onUnmounted(() => {
  window.removeEventListener("beforeunload", onBeforeUnload);
  window.removeEventListener("pagehide", onPageHide);
  document.removeEventListener("visibilitychange", onVisibilityChange);
});

const pageTitle = computed(() => {
  let ret = "";

  switch (purchase.status) {
    case PurchaseStatus.Started:
      ret = "Ticket Sale";
      break;
    case PurchaseStatus.Completed:
      ret = "Purchase Confirmation";
      break;
    case null:
      ret = purchase.$load.wasSuccessful === false ? "Error" : "Loading";
      break;
    default:
      ret = propDisplay(purchase, "status") + " Purchase";
      break;
  }

  if (purchase.confirmationCode) {
    ret = ret + " - " + purchase.confirmationCode;
  }

  if (purchase.eventDate?.eventName) {
    ret = ret + " - " + purchase.eventDate.eventName;
  }

  return ret;
});

useTitle(pageTitle.value);

purchase.$load.onFulfilled((res) => {
  if (suppressPurchaseScroll.value) {
    suppressPurchaseScroll.value = false; // #3470: consume + reset
    return;
  }
  if (res.wasSuccessful) scrollToTop();
});

purchase.$load(props.publicId);

onBeforeRouteLeave((to, _, next) => {
  if (
    to.name == "ticket-purchase-redirecting" ||
    purchase.status !== PurchaseStatus.Started
  ) {
    return next();
  }

  // The confirm function on iOS safari instantly returns false after the back button has been pressed.
  // So, we'll time how long it takes to run. If it takes beyond some arbitrary amount of time,
  // it must have actually prompted the user, which pauses JS execution.
  // If it seems to have completed instantly, it probably didn't prompt the user.
  const startTime = new Date();
  const confirmationResult = confirm(
    "Are you sure you wish to navigate away? Your purchase is not yet complete.",
  );
  const endTime = new Date();
  const wasConfirmationPrompted =
    differenceInMilliseconds(endTime, startTime) > 300;

  if (confirmationResult || !wasConfirmationPrompted) {
    userConfirmedLeave = true;
    apiClient.abandonPurchase(props.publicId);
    return next();
  }

  return false;
});
</script>
